﻿import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Book, Code, Terminal, Shield, Globe, Zap, Search, Sparkles, Layers } from 'lucide-react';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { useModuleData } from '@/hooks/useModuleData';
import { LoadingSpinner, GlassCard, IconBox } from './SharedUI';

export function Docs({ t }: { t: any }) {
  const { data, loading } = useModuleData<any>('/api/modules/docs');
  const [activeTab, setActiveTab] = React.useState('philosophy');

  if (loading) return <LoadingSpinner />;

  const tabs = [
    { id: 'philosophy', title: t.corePhilosophy, icon: <Sparkles size={18} /> },
    { id: 'hosts', title: t.smartHostProgram, icon: <Layers size={18} /> },
    { id: 'clients', title: t.multiClientTitle, icon: <Globe size={18} /> },
    { id: 'getting-started', title: t.gettingStarted || '快速开始', icon: <Book size={18} /> },
    { id: 'api', title: t.apiReference || 'API 参考', icon: <Code size={18} /> },
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-12">
      <div className="text-center space-y-6">
        <h1 className="text-6xl font-bold tracking-tighter glow-text">{data?.title || t.docs}</h1>
        <p className="text-xl text-white/60 max-w-2xl mx-auto">{t.docsTagline || 'Master the 小枫 protocol and build advanced Agent architectures.'}</p>
        <div className="max-w-md mx-auto relative">
          <Input placeholder={t.searchDocs || "搜索文档..."} className="bg-white/5 border-white/10 rounded-2xl p-6 h-auto text-lg pl-12" />
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={20} />
        </div>
      </div>

      {/* Secondary Navigation */}
      <div className="flex flex-wrap justify-center gap-4 border-b border-white/5 pb-8">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all ${
              activeTab === tab.id 
                ? 'bg-celestial-nebula text-white shadow-[0_0_20px_rgba(255,45,85,0.3)]' 
                : 'bg-white/5 text-white/40 hover:bg-white/10'
            }`}
          >
            {tab.icon}
            <span className="font-bold text-sm uppercase tracking-widest">{tab.title}</span>
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="space-y-12"
        >
          {activeTab === 'philosophy' && (
            <div className="space-y-12">
              <div className="text-center space-y-4">
                <h2 className="text-4xl font-bold tracking-tighter">{t.corePhilosophy}</h2>
                <p className="text-white/60 max-w-2xl mx-auto">{t.philosophyDesc}</p>
              </div>
              <div className="grid md:grid-cols-3 gap-8">
                <PhilosophyCard 
                  icon={<Shield className="text-celestial-nebula" size={32} />} 
                  title={t.philosophyPillar1} 
                  desc={t.philosophyPillar1Desc} 
                />
                <PhilosophyCard 
                  icon={<Zap className="text-celestial-saturn" size={32} />} 
                  title={t.philosophyPillar2} 
                  desc={t.philosophyPillar2Desc} 
                />
                <PhilosophyCard 
                  icon={<Globe className="text-celestial-glow" size={32} />} 
                  title={t.philosophyPillar3} 
                  desc={t.philosophyPillar3Desc} 
                />
              </div>
            </div>
          )}

          {activeTab === 'hosts' && (
            <div className="space-y-16">
              <div className="text-center space-y-4">
                <h2 className="text-4xl font-bold tracking-tighter glow-text">{t.smartHostProgram}</h2>
                <p className="text-white/60 max-w-2xl mx-auto italic">"{t.everythingCanBeLumi}"</p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div className="space-y-6">
                  <h3 className="text-2xl font-bold text-celestial-saturn underline decoration-celestial-saturn/30 underline-offset-8">{t.hostArchitecture || '宿主架构'}</h3>
                  <p className="text-lg text-white/70 leading-relaxed">
                    {t.hostArchitectureDesc || "The Smart Host Program defines a hardware-agnostic communication protocol that allows the 小枫 Neural Core to 'inhabit' existing physical systems."}
                  </p>
                  <ul className="space-y-4">
                    <li className="flex gap-4">
                      <div className="w-6 h-6 rounded-full bg-celestial-saturn/20 flex items-center justify-center text-celestial-saturn font-bold text-xs mt-1">1</div>
                      <p className="text-sm text-white/60"><strong className="text-white">{t.neuralEncapsulationLabel || 'Neural Encapsulation:'}</strong> {t.neuralEncapsulationDesc || 'Wrapping existing APIs into 小枫-compatible intent streams.'}</p>
                    </li>
                    <li className="flex gap-4">
                      <div className="w-6 h-6 rounded-full bg-celestial-saturn/20 flex items-center justify-center text-celestial-saturn font-bold text-xs mt-1">2</div>
                      <p className="text-sm text-white/60"><strong className="text-white">{t.symbioticFeedbackLabel || 'Symbiotic Feedback:'}</strong> {t.symbioticFeedbackDesc || '来自主机的实时感官数据促进本地智能体演化。'}</p>
                    </li>
                  </ul>
                </div>
                <GlassCard className="p-8 aspect-video flex flex-col justify-center items-center text-center space-y-6">
                   <Layers size={64} className="text-celestial-saturn animate-pulse" />
                   <div className="space-y-2">
                     <h4 className="font-bold">{t.protocolStatusBeta || '协议状态：测试版'}</h4>
                     <p className="text-xs text-white/40">{t.activeHostsStats || '活跃主机：12,400+ | 分类：玩具、工业、可穿戴'}</p>
                   </div>
                </GlassCard>
              </div>
            </div>
          )}

          {activeTab === 'clients' && (
            <div className="space-y-16">
              <div className="text-center space-y-4">
                <h2 className="text-4xl font-bold tracking-tighter glow-text">{t.multiClientTitle}</h2>
                <p className="text-white/60 max-w-2xl mx-auto">{t.syncMultiDesc || '你所有维度的无缝同步。'}</p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <GlassCard className="p-8 space-y-4 border-t-2 border-t-celestial-saturn">
                  <div className="flex items-center gap-3">
                    <Terminal className="text-celestial-saturn" size={24} />
                    <h3 className="text-xl font-bold">{t.desktopNode}</h3>
                  </div>
                  <p className="text-sm text-white/40 leading-relaxed">{t.desktopDesc}</p>
                  <div className="pt-4 space-y-2">
                    <div className="flex items-center gap-2 text-xs text-white/60">
                      <Zap size={12} className="text-celestial-saturn" />
                      <span>{t.desktopPlatforms || 'Windows / macOS / Linux（Electron 运行时）'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-white/60">
                      <Zap size={12} className="text-celestial-saturn" />
                      <span>{t.desktopHardwareAccess || '本地推理的直接硬件访问'}</span>
                    </div>
                  </div>
                </GlassCard>

                <GlassCard className="p-8 space-y-4 border-t-2 border-t-celestial-nebula">
                  <div className="flex items-center gap-3">
                    <Globe className="text-celestial-nebula" size={24} />
                    <h3 className="text-xl font-bold">{t.mobilePerception}</h3>
                  </div>
                  <p className="text-sm text-white/40 leading-relaxed">{t.mobileDesc}</p>
                  <div className="pt-4 space-y-2">
                    <div className="flex items-center gap-2 text-xs text-white/60">
                      <Zap size={12} className="text-celestial-nebula" />
                      <span>{t.mobilePlatforms || 'iOS / Android（Capacitor 运行时）'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-white/60">
                      <Zap size={12} className="text-celestial-nebula" />
                      <span>{t.multimodalSensing || '多模态感知与语音同步'}</span>
                    </div>
                  </div>
                </GlassCard>
              </div>

              <div className="p-12 bg-white/5 rounded-[32px] border border-white/10 space-y-8">
                <div className="text-center space-y-2">
                  <h3 className="text-2xl font-bold">{t.syncProtocol}</h3>
                  <p className="text-sm text-white/40">{t.syncProtocolDesc}</p>
                </div>
                <div className="flex flex-col md:flex-row items-center justify-center gap-8">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center text-celestial-saturn">
                      <Terminal size={32} />
                    </div>
                    <span className="text-xs font-bold uppercase tracking-tighter">{t.brainNode || '大脑节点'}</span>
                  </div>
                  <div className="flex-1 h-px bg-gradient-to-r from-celestial-saturn via-white/20 to-celestial-nebula hidden md:block min-w-[100px]" />
                  <div className="px-4 py-2 rounded-full border border-white/10 bg-white/5 text-xs font-mono">
                    AES-256-GCM / P2P Mesh
                  </div>
                  <div className="flex-1 h-px bg-gradient-to-r from-celestial-nebula via-white/20 to-celestial-saturn hidden md:block min-w-[100px]" />
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center text-celestial-nebula">
                      <Globe size={32} />
                    </div>
                    <span className="text-xs font-bold uppercase tracking-tighter">{t.senseNode || '感知节点'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'getting-started' && (
            <div className="grid md:grid-cols-3 gap-8">
              <DocCard
                icon={<Book className="text-celestial-mars" />}
                title={t.gettingStarted || '快速开始'}
                desc={t.gettingStartedDesc || '学习智能体生成和本地节点设置的基础。'}
                links={[t.installDoc || '安装', t.firstAgentDoc || '首个智能体', t.syncingDevicesDoc || '同步设备']}
              />
              <DocCard
                icon={<Zap className="text-celestial-saturn" />}
                title={t.quickStart || '快速启动'}
                desc={t.quickStartDesc || '5 分钟内上手运行。'}
                links={[t.cliSetupDoc || 'CLI 配置', t.helloWorldDoc || '你好世界', t.nodeConfigDoc || '节点配置']}
              />
              <DocCard
                icon={<Globe className="text-celestial-glow" />}
                title={t.ecosystemDoc || '生态系统'}
                desc={t.ecosystemDocDesc || 'Explore the 小枫 distributed network.'}
                links={[t.meshBasicsDoc || '网格基础', t.p2pSyncDoc || 'P2P 同步', t.securityDoc || '安全']}
              />
            </div>
          )}

          {activeTab === 'api' && (
            <div className="grid md:grid-cols-3 gap-8">
              <DocCard
                icon={<Code className="text-celestial-saturn" />}
                title={t.apiReference || 'API 参考'}
                desc={t.apiReferenceDesc || 'Deep dive into the 小枫 local SDK and neural synthesis API.'}
                links={[t.authDoc || '身份认证', t.dataStreamsDoc || '数据流', t.memoryCoreDoc || '记忆核心']}
              />
              <DocCard
                icon={<Terminal className="text-celestial-glow" />}
                title={t.cliCommands || 'CLI 命令'}
                desc={t.cliCommandsDesc || 'Full reference for the 小枫 command line interface.'}
                links={['xiaofeng generate', 'xiaofeng sync', 'xiaofeng status']}
              />
              <DocCard
                icon={<Shield className="text-celestial-mars" />}
                title={t.securityApi || '安全 API'}
                desc={t.securityApiDesc || '管理加密和节点权限。'}
                links={[t.keyManagementDoc || '密钥管理', t.accessControlDoc || '访问控制', t.privacyLevelsDoc || '隐私级别']}
              />
            </div>
          )}

          {activeTab === 'advanced' && (
            <div className="grid md:grid-cols-3 gap-8">
              <DocCard
                icon={<Terminal className="text-celestial-glow" />}
                title={t.advancedGuides || '高级指南'}
                desc={t.advancedGuidesDesc || '构建复杂的多智能体系统和自定义知识图谱。'}
                links={[t.orchestrationDoc || '编排', t.customLLMsDoc || '自定义 LLM', t.securityRulesDoc || '安全规则']}
              />
              <DocCard
                icon={<Zap className="text-celestial-saturn" />}
                title={t.performanceDoc || '性能'}
                desc={t.performanceDocDesc || '优化节点以实现高速推理。'}
                links={[t.gpuAccelerationDoc || 'GPU 加速', t.memoryManagementDoc || '内存管理', t.cachingDoc || '缓存']}
              />
              <DocCard
                icon={<Globe className="text-celestial-nebula" />}
                title={t.globalMeshDoc || '全球网格'}
                desc={t.globalMeshDocDesc || '在分布式网格中扩展你的智能体。'}
                links={[t.swarmLogicDoc || '群体逻辑', t.crossNodeSyncDoc || '跨节点同步', t.latencyDoc || '延迟']}
              />
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-12">
            {data?.sections?.map((section: any) => (
              <GlassCard key={section.id} className="space-y-6" hoverEffect={false}>
                <h3 className="text-2xl font-bold tracking-tighter">{section.title}</h3>
                <div className="space-y-4 text-white/60 leading-relaxed">
                  <p>{section.content}</p>
                  {section.id === 1 && (
                    <div className="p-6 bg-white/5 rounded-2xl border border-white/10 font-mono text-sm overflow-x-auto">
                      <code>
                        xiaofeng generate --name "MyAgent" --data ./docs/knowledge.pdf --privacy high
                      </code>
                    </div>
                  )}
                </div>
              </GlassCard>
            ))}
            
            {!data?.sections && activeTab === 'philosophy' && (
              <>
                <GlassCard className="space-y-6" hoverEffect={false}>
                  <h3 className="text-2xl font-bold tracking-tighter">{t.neuralSynthesisProtocol || '神经合成协议'}</h3>
                  <div className="space-y-4 text-white/60 leading-relaxed">
                    <p>{t.neuralSynthesisProtocolDesc || "The 小枫 protocol uses distributed neural synthesis to build Agent intelligence. This process is entirely local, ensuring that your data never leaves your node."}</p>
                    <div className="p-6 bg-white/5 rounded-2xl border border-white/10 font-mono text-sm overflow-x-auto">
                      <code>
                        xiaofeng generate --name "MyAgent" --data ./docs/knowledge.pdf --privacy high
                      </code>
                    </div>
                    <p>{t.neuralSynthesisPrivacyDesc || '通过定义高隐私级别，协议将对所有神经权重计算使用高级加密和仅本地处理。'}</p>
                  </div>
                </GlassCard>

                <GlassCard className="space-y-6" hoverEffect={false}>
                  <h3 className="text-2xl font-bold tracking-tighter">{t.securityPrivacy || '安全与隐私'}</h3>
                  <div className="space-y-4 text-white/60 leading-relaxed">
                    <p>{t.securityPrivacyDesc || 'Privacy is the core of 小枫. Our security architecture is built on three pillars:'}</p>
                    <ul className="space-y-3">
                      <li className="flex items-center gap-3"><Zap size={16} className="text-celestial-saturn" /> {t.localFirstProcessing || '本地优先数据处理'}</li>
                      <li className="flex items-center gap-3"><Shield size={16} className="text-celestial-mars" /> {t.endToEndEncryption || '端到端节点加密'}</li>
                      <li className="flex items-center gap-3"><Globe size={16} className="text-celestial-glow" /> {t.decentralizedIdentity || '去中心化身份（DID）'}</li>
                    </ul>
                  </div>
                </GlassCard>
              </>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function PhilosophyCard({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
  return (
    <GlassCard className="p-8 rounded-3xl space-y-4 text-center border border-white/5 hover:border-celestial-nebula/30 transition-colors">
      <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-2">
        {icon}
      </div>
      <h3 className="text-xl font-bold tracking-tight">{title}</h3>
      <p className="text-sm text-white/40 leading-relaxed">{desc}</p>
    </GlassCard>
  );
}

function DocCard({ icon, title, desc, links }: { icon: React.ReactNode; title: string; desc: string; links: string[] }) {
  return (
    <GlassCard className="space-y-6 group">
      <IconBox icon={icon} />
      <div className="space-y-2">
        <h3 className="text-xl font-bold tracking-tighter">{title}</h3>
        <p className="text-sm text-white/40 leading-relaxed">{desc}</p>
      </div>
      <ul className="space-y-2">
        {links.map((link, i) => (
          <li key={i} className="text-sm text-celestial-saturn hover:underline cursor-pointer flex items-center gap-2">
            <Zap size={12} />
            {link}
          </li>
        ))}
      </ul>
    </GlassCard>
  );
}
